import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const AdminMessaging = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [filter, setFilter] = useState('all'); // all, unread, pending, replied
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    unread: 0,
    pending: 0,
    replied: 0
  });

  useEffect(() => {
    loadMessages();
    loadStats();
  }, []);

  const loadMessages = async () => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.MESSAGES}/admin`, {
        headers: {
          'admin-email': adminUser.email
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      } else {
        console.error('Failed to load messages');
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.MESSAGES}/admin/stats`, {
        headers: {
          'admin-email': adminUser.email
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      await fetch(`${API_ENDPOINTS.MESSAGES}/admin/${messageId}/read`, {
        method: 'PUT',
        headers: {
          'admin-email': adminUser.email
        }
      });

      // Update local state
      setMessages(messages.map(msg =>
        msg.id === messageId ? { ...msg, isRead: true } : msg
      ));
      loadStats(); // Refresh stats
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedMessage) return;

    setIsReplying(true);
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.MESSAGES}/admin/${selectedMessage.id}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'admin-email': adminUser.email
        },
        body: JSON.stringify({ reply: replyText })
      });

      if (response.ok) {
        const result = await response.json();
        // Update local state
        setMessages(messages.map(msg =>
          msg.id === selectedMessage.id
            ? {
                ...msg,
                adminReply: result.reply,
                replyTimestamp: result.replyTimestamp,
                status: 'replied',
                isRead: true
              }
            : msg
        ));
        setReplyText('');
        setSelectedMessage(null);
        loadStats(); // Refresh stats
        alert('Reply sent successfully!');
      } else {
        alert('Failed to send reply');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Failed to send reply');
    } finally {
      setIsReplying(false);
    }
  };

  const archiveMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to archive this message?')) return;

    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      await fetch(`${API_ENDPOINTS.MESSAGES}/admin/${messageId}`, {
        method: 'DELETE',
        headers: {
          'admin-email': adminUser.email
        }
      });

      // Remove from local state
      setMessages(messages.filter(msg => msg.id !== messageId));
      loadStats(); // Refresh stats
    } catch (error) {
      console.error('Error archiving message:', error);
    }
  };

  const filteredMessages = messages.filter(message => {
    const matchesFilter = filter === 'all' ||
      (filter === 'unread' && !message.isRead) ||
      (filter === 'pending' && message.status === 'pending') ||
      (filter === 'replied' && message.status === 'replied');

    const matchesSearch = searchTerm === '' ||
      message.senderName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.senderEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.subject.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <span className="mr-3">💬</span>
              Message Management
            </h1>
            <p className="text-gray-600 mt-2">Manage contact messages and respond to inquiries</p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-300"
          >
            Back to Dashboard
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Messages</p>
                <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
              </div>
              <span className="text-3xl">📧</span>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Unread</p>
                <p className="text-2xl font-bold text-red-600">{stats.unread}</p>
              </div>
              <span className="text-3xl">🔴</span>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <span className="text-3xl">Loading</span>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Replied</p>
                <p className="text-2xl font-bold text-green-600">{stats.replied}</p>
              </div>
              <span className="text-3xl">Loading</span>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: 'All Messages', count: stats.total },
                { key: 'unread', label: 'Unread', count: stats.unread },
                { key: 'pending', label: 'Pending', count: stats.pending },
                { key: 'replied', label: 'Replied', count: stats.replied }
              ].map(({ key, label, count }) => (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  className={`px-4 py-2 rounded-lg font-medium transition duration-300 ${
                    filter === key
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {label} ({count})
                </button>
              ))}
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-gray-600">Search</span>
              <input
                type="text"
                placeholder="Search messages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Messages List */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {filteredMessages.length === 0 ? (
            <div className="p-8 text-center">
              <span className="text-6xl mb-4 block">Inbox</span>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No messages found</h3>
              <p className="text-gray-500">
                {filter === 'all' && searchTerm === ''
                  ? 'No messages have been received yet.'
                  : 'Try adjusting your filters or search terms.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredMessages.map((message) => (
                <div
                  key={message.id}
                  className={`p-6 hover:bg-gray-50 transition duration-300 cursor-pointer ${
                    !message.isRead ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  }`}
                  onClick={() => setSelectedMessage(message)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="font-semibold text-gray-900">{message.senderName}</h3>
                        <span className="text-sm text-gray-500">({message.senderEmail})</span>
                        {!message.isRead && (
                          <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full animate-pulse">
                            New
                          </span>
                        )}
                      </div>
                      <h4 className="font-medium text-gray-800 mb-1">{message.subject}</h4>
                      <p className="text-gray-600 text-sm line-clamp-2">{message.message}</p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                        <span>📅 {formatDate(message.timestamp)}</span>
                        <span className={`px-2 py-1 rounded-full ${
                          message.status === 'replied'
                            ? 'bg-green-100 text-green-800'
                            : message.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {message.status}
                        </span>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      {!message.isRead && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(message.id);
                          }}
                          className="text-blue-600 hover:text-blue-800 text-sm underline"
                        >
                          Mark Read
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          archiveMessage(message.id);
                        }}
                        className="text-red-600 hover:text-red-800 text-sm underline"
                      >
                        Archive
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Message Detail Modal */}
        {selectedMessage && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900">Message Details</h2>
                  <button
                    onClick={() => setSelectedMessage(null)}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="p-6 max-h-[60vh] overflow-y-auto">
                {/* Message Header */}
                <div className="mb-6">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-xl font-semibold text-gray-900">{selectedMessage.senderName}</h3>
                    <span className="text-gray-500">({selectedMessage.senderEmail})</span>
                  </div>
                  <h4 className="text-lg font-medium text-gray-800 mb-2">{selectedMessage.subject}</h4>
                  <p className="text-sm text-gray-500">📅 {formatDate(selectedMessage.timestamp)}</p>
                </div>

                {/* Original Message */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h5 className="font-semibold text-gray-800 mb-2">Message:</h5>
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.message}</p>
                </div>

                {/* Admin Reply */}
                {selectedMessage.adminReply ? (
                  <div className="bg-green-50 rounded-lg p-4 mb-6 border-l-4 border-green-500">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="font-semibold text-green-800">Admin Reply:</span>
                      <span className="text-sm text-green-600">📅 {formatDate(selectedMessage.replyTimestamp)}</span>
                    </div>
                    <p className="text-green-800 whitespace-pre-wrap">{selectedMessage.adminReply}</p>
                  </div>
                ) : (
                  /* Reply Form */
                  <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
                    <h5 className="font-semibold text-blue-800 mb-3">Send Reply:</h5>
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply here..."
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                    <div className="flex justify-end space-x-3 mt-4">
                      <button
                        onClick={() => setSelectedMessage(null)}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={sendReply}
                        disabled={isReplying || !replyText.trim()}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isReplying ? 'Sending...' : 'Send Reply'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMessaging;



